import { embeddedAppPath } from './deployPaths'
import type { Experience } from './experience'
import { readPersistedExperienceForToken } from './persistedExperience'
import { shellPathEntriesEnabled } from './shellPathEntries'

export type DefaultExperience = 'legacy' | 'new'

const normalizedDefault = import.meta.env.VITE_DEFAULT_EXPERIENCE?.trim().toLowerCase()

export const defaultExperience: DefaultExperience =
  normalizedDefault === 'new' ? 'new' : 'legacy'

function stripTrailingSlash(s: string): string {
  return s.endsWith('/') ? s.slice(0, -1) : s
}

export function getLegacyAppRoot(): string {
  const full = import.meta.env.VITE_LEGACY_APP_ORIGIN?.trim()
  if (full) return stripTrailingSlash(full)
  const path = import.meta.env.VITE_LEGACY_APP_PATH?.trim() || embeddedAppPath('legacy')
  const p = path.startsWith('/') ? path : `/${path}`
  return stripTrailingSlash(`${window.location.origin}${p}`)
}

export function getNewAppRoot(): string {
  const full = import.meta.env.VITE_NEW_APP_ORIGIN?.trim()
  if (full) return stripTrailingSlash(full)
  const path = import.meta.env.VITE_NEW_APP_PATH?.trim() || embeddedAppPath('new')
  const p = path.startsWith('/') ? path : `/${path}`
  return stripTrailingSlash(`${window.location.origin}${p}`)
}

export function appRootForExperience(experience: Experience): string {
  return experience === 'legacy' ? getLegacyAppRoot() : getNewAppRoot()
}

export function iframeUrlForToken(appRoot: string, token: string): string {
  const root = appRoot.endsWith('/') ? appRoot : `${appRoot}/`
  const url = new URL(encodeURIComponent(token), root)
  if (shellPathEntriesEnabled()) {
    url.searchParams.set('embed', '1')
  }
  return url.toString()
}

export function iframeUrlForExperience(experience: Experience, token?: string): string {
  const root = appRootForExperience(experience)
  const trimmed = token?.trim()
  if (trimmed) return iframeUrlForToken(root, trimmed)
  const base = root.endsWith('/') ? root : `${root}/`
  const url = new URL(base)
  if (shellPathEntriesEnabled()) {
    url.searchParams.set('embed', '1')
  }
  return url.toString()
}

export function iframeExpiredUrlForExperience(experience: Experience): string {
  const root = appRootForExperience(experience)
  const base = root.endsWith('/') ? root : `${root}/`
  const url = new URL('expired', base)
  if (shellPathEntriesEnabled()) {
    url.searchParams.set('embed', '1')
  }
  return url.toString()
}

export function experienceWhenShellAuthUnavailable(
  pathExperience?: Experience,
  contactToken?: string,
): Experience {
  if (pathExperience) return pathExperience
  const trimmed = contactToken?.trim()
  if (trimmed) {
    const persisted = readPersistedExperienceForToken(trimmed)
    if (persisted) return persisted
  }
  return defaultExperience
}
